import { AVATAR_CROP_MODAL_ID } from '@common/constants'
import { staticFile } from '@common/utils'
import Modal from '@components/common/modal'
import Spinner from '@components/common/spinner'
import { FALLBACK_IMAGE } from '@lib/constants/gamification'
import cropImage from '@lib/utils/crop-image'
import { t, Trans } from '@lingui/macro'
import {
  UploadyContext,
  useItemErrorListener,
  useItemFinalizeListener,
  withRequestPreSendUpdate,
} from '@rpldy/uploady'
import cogoToast from 'cogo-toast'
import Router from 'next/router'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { X } from 'react-feather'
import ReactCrop from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import swal from 'sweetalert'

const MIN_CROP_WIDTH = 200

type Props = {
  id: string
  url: string
  isFallback: boolean
  type: string
  updateRequest: any
  requestData: any
  previewMethods: React.MutableRefObject<any>
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
}

/** i18n support added */
const AvatarCrop = withRequestPreSendUpdate(
  ({
    id,
    url,
    isFallback,
    type,
    updateRequest,
    requestData,
    previewMethods,
    isOpen,
    setIsOpen,
  }: Props) => {
    const [crop, setCrop] = useState(null)
    const [loading, setLoading] = useState(false)
    const uploady = useContext(UploadyContext)
    const imageRef = useRef(null)

    useItemFinalizeListener(async (item) => {
      setLoading(false)
      setIsOpen(false)

      if (item.uploadStatus === 200) {
        cogoToast.success(t`Profile picture updated successfully.`)
        Router.reload()
      }

      if (previewMethods.current?.clear) {
        previewMethods.current.clear()
      }
    }, id)

    useItemErrorListener(() => {
      swal({
        closeOnClickOutside: false,
        closeOnEsc: false,
        icon: 'error',
        title: t`Oops!`,
        text: t`Something went wrong, please contact support if error persists.`,
      })
    })

    const onUploadCrop = useCallback(async () => {
      setLoading(true)

      if (!crop?.height || !crop?.width) {
        swal({
          closeOnClickOutside: false,
          closeOnEsc: false,
          icon: 'warning',
          title: t`Oops!`,
          text: t`Please crop the image.`,
        })
        setLoading(false)
        return
      }

      if (crop?.height < 200 || crop?.width < 200) {
        swal({
          closeOnClickOutside: false,
          closeOnEsc: false,
          icon: 'warning',
          title: t`Oops!`,
          text: t`Please select larger image.`,
        })
        setLoading(false)
        return
      }

      if (updateRequest && (crop?.height || crop?.width)) {
        requestData.items[0].file = await cropImage(
          requestData.items[0].file,
          crop,
          imageRef
        )
        updateRequest({ items: requestData.items })
      }
    }, [url, requestData, updateRequest, crop])

    const onUploadCancel = useCallback(() => {
      updateRequest(false)
      setIsOpen(false)
      if (previewMethods.current?.clear) {
        previewMethods.current.clear()
      }
      uploady.abort()
    }, [updateRequest, previewMethods])

    const centerCrop = useCallback((img) => {
      imageRef.current = img
      const aspect = 1
      const width = (img.width < img.height ? img.width : img.height) / 1.2
      const height = (img.width > img.height ? img.height : img.width) / 1.2
      const y = (img.height - height) / 2
      const x = (img.width - width) / 2

      setCrop({
        unit: 'px',
        width,
        height,
        x,
        y,
        aspect,
      })
      return false
    }, [])

    useEffect(() => {
      if (isFallback || type !== 'image') raiseTypeError()
    }, [])

    const raiseTypeError = () => {
      setIsOpen(false)
      if (previewMethods.current?.clear) {
        previewMethods.current.clear()
      }
      uploady.abort()
      swal({
        closeOnClickOutside: false,
        closeOnEsc: false,
        icon: 'error',
        title: t`Oops!`,
        text: t`Image should be of format .jpg, .jpeg, .png or .webp`,
      })
    }

    return (
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        width="sm:max-w-5xl"
        id={AVATAR_CROP_MODAL_ID}
      >
        <div className="overflow-auto max-h-screen">
          <div className="flex justify-end">
            <button className="focus:outline-none" onClick={onUploadCancel}>
              <X color="#666" className="stroke-1" />
            </button>
          </div>
          <h2 className="font-600 text-2xl text-center text-grey">
            <Trans>Update profile picture</Trans>
          </h2>
          {isFallback || type !== 'image' ? (
            <div className="flex flex-col items-center">
              <img src={staticFile(FALLBACK_IMAGE)} alt="fallback img" />
              <p className="text-xl text-grey font-500 mt-3">
                <Trans>Please upload an image file.</Trans>
              </p>
              <button
                onClick={onUploadCancel}
                className="btn btn-orange text-white font-600 text-lg py-2 my-2"
              >
                <Trans>Close</Trans>
              </button>
            </div>
          ) : requestData ? (
            <div className="p-5 overflow-auto max-h-screen">
              <div className="flex flex-col items-center">
                <ReactCrop
                  src={url}
                  crop={crop}
                  onChange={setCrop}
                  onComplete={setCrop}
                  onImageLoaded={centerCrop}
                  minWidth={MIN_CROP_WIDTH}
                  imageStyle={{ maxHeight: '70vh' }}
                />
              </div>
              <CropButtons
                onUploadCancel={onUploadCancel}
                onUploadCrop={onUploadCrop}
                loading={loading}
              />
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <img src={url} alt="uploading img" />
              <p className="text-xl text-grey font-500 mt-3">
                <Trans>Something went wrong.</Trans>
              </p>
              <button
                onClick={onUploadCancel}
                className="btn btn-orange text-white font-600 text-lg py-2 my-2"
              >
                <Trans>Close</Trans>
              </button>
            </div>
          )}
        </div>
      </Modal>
    )
  }
)

type CropButtonsType = {
  onUploadCancel: () => void
  onUploadCrop: () => void
  loading: boolean
}

const CropButtons = ({
  onUploadCancel,
  onUploadCrop,
  loading,
}: CropButtonsType) => {
  return (
    <div className="flex space-x-5 mt-5">
      <button
        className={`btn btn-orange text-white font-600 text-lg py-2 my-2 ${
          loading && 'opacity-50 cursor-not-allowed'
        }`}
        onClick={onUploadCrop}
        disabled={loading}
      >
        {loading ? (
          <Spinner classes="text-white h-5 w-5" />
        ) : (
          t`Upload profile picture`
        )}
      </button>
      <button
        className={`btn border border-orange font-600 text-orange text-lg py-2 my-2 hover:bg-orange-100 active:bg-orange-200 ${
          loading && 'opacity-50 cursor-not-allowed'
        }`}
        onClick={onUploadCancel}
        disabled={loading}
      >
        <Trans>Cancel</Trans>
      </button>
    </div>
  )
}

export default AvatarCrop
